import {
  applyCascadeDisable,
  applyCascadeDisableAll,
  getDependencyState,
} from '@config/toggleDependencies';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { isFactorDeprecated } from '@parlay-picker/shared/deprecatedFactors';
import {
  canUseFactor,
  countEnabledFactors,
  countsTowardLimit,
  getFactor,
} from '@parlay-picker/shared/factorRegistry';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import { factorLimitFor } from '@parlay-picker/shared/tierInputPolicy';
import { useAnalysisConfigStore } from '@stores/analysisConfigStore';
import type { AnalysisConfig } from '@views/picks/constants';
import { DEFAULT_ANALYSIS_CONFIG } from '@views/picks/constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PROP_TYPE_OPTIONS } from './configConstants';
import type { ExpandedSections } from './configTypes';

export interface UseAnalysisConfigPanelOptions {
  isOpen: boolean;
  onClose: () => void;
  config: AnalysisConfig;
  setConfig: React.Dispatch<React.SetStateAction<AnalysisConfig>>;
}

const STAT_WINDOW_KEYS = ['l5Average', 'l10Average', 'l20Average', 'seasonAverage'] as const;

export function useAnalysisConfigPanel({
  isOpen,
  onClose,
  config,
  setConfig,
}: UseAnalysisConfigPanelOptions) {
  const { tier } = useFeatureAccess();
  const [upgradeFeature, setUpgradeFeature] = useState<GatedFeature | null>(null);

  const factorLimit = factorLimitFor(tier);
  const enabledFactors = countEnabledFactors(config);
  const atFactorLimit = enabledFactors >= factorLimit;
  const overFactorLimit = enabledFactors > factorLimit;

  // Track which sections are expanded - load from localStorage or use defaults
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>(() => {
    try {
      const saved = localStorage.getItem('configPanelExpanded');
      if (saved) return JSON.parse(saved) as ExpandedSections;
    } catch {
      // Ignore parse errors
    }
    return {
      outputOptions: true,
      banList: true,
      parlayStrategy: false,
      propTypes: false,
      playerStats: false,
      advancedStats: false,
      matchupData: false,
      opponentDefense: false,
      gameScript: false,
      trendAnalysis: false,
      bettingHistory: false,
      otherFactors: false,
      sharpMoney: false,
      ml: false,
      parlayIntelligence: false,
    };
  });

  // Save expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('configPanelExpanded', JSON.stringify(expandedSections));
  }, [expandedSections]);

  // Handle Escape key to close panel
  const handleKeyDown = useCallback(
    (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const toggleSection = (sectionKey: string): void => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const toggleAllSections = (): void => {
    setExpandedSections((prev) => {
      const allExpanded = Object.values(prev).every((v) => v);
      const newState: ExpandedSections = { ...prev };
      Object.keys(prev).forEach((key) => {
        newState[key] = !allExpanded;
      });
      return newState;
    });
  };

  // Factor limit helper: returns disabled/disabledReason props for factor toggles
  const factorLimitProps = (
    checked: boolean,
    existingDisabled = false,
    existingReason?: string,
  ) => ({
    disabled: existingDisabled || (!checked && atFactorLimit),
    disabledReason:
      !checked && atFactorLimit && !existingDisabled
        ? `You've reached your ${factorLimit}-factor limit — disable another factor first`
        : existingReason,
  });

  // Composed toggle props: merges dependency state + factor limit + optional feature lock
  const composedToggleProps = (
    dotPath: string,
    checked: boolean,
    featureLocked = false,
    featureLockedReason?: string,
  ) => {
    const dep = getDependencyState(config, dotPath);
    if (dep.disabled) return { disabled: true, disabledReason: dep.disabledReason };
    if (featureLocked)
      return {
        disabled: true,
        disabledReason: featureLockedReason ?? 'Upgrade required',
      };
    return factorLimitProps(checked);
  };

  // Check if a stat window is the last one enabled (can't disable it)
  const isLastStatWindow = (key: (typeof STAT_WINDOW_KEYS)[number]): boolean => {
    const ps = config.playerStats || {};
    if (ps[key] === false) return false; // already off, not the last
    return STAT_WINDOW_KEYS.filter((k) => ps[k] !== false).length <= 1;
  };

  const updateConfig = (section: string, key: string, value: boolean | number | string): void => {
    setConfig((prev) => {
      const currentSection = prev[section];
      if (typeof currentSection !== 'object' || currentSection === null) return prev;

      // Prevent disabling the last stat window — at least 1 must stay enabled
      if (
        section === 'playerStats' &&
        value === false &&
        STAT_WINDOW_KEYS.includes(key as (typeof STAT_WINDOW_KEYS)[number])
      ) {
        const ps = prev.playerStats || {};
        const enabledCount = STAT_WINDOW_KEYS.filter((k) => ps[k] !== false).length;
        if (enabledCount <= 1) return prev; // block — this is the last one
      }

      const updated = {
        ...prev,
        [section]: {
          ...currentSection,
          [key]: value,
        },
      } as AnalysisConfig;

      // Apply cascade disabling
      return applyCascadeDisable(updated, `${section}.${key}`);
    });
  };

  const resetToSaved = (): void => {
    const savedConfig = useAnalysisConfigStore.getState().savedConfig;
    setConfig(savedConfig);
  };

  const handleResetFactorsToDefaults = () => {
    setConfig((prev) => ({
      ...prev,
      playerStats: DEFAULT_ANALYSIS_CONFIG.playerStats,
      advancedStats: DEFAULT_ANALYSIS_CONFIG.advancedStats,
      matchupData: DEFAULT_ANALYSIS_CONFIG.matchupData,
      opponentDefense: DEFAULT_ANALYSIS_CONFIG.opponentDefense,
      gameScript: DEFAULT_ANALYSIS_CONFIG.gameScript,
      efficiencyMetrics: DEFAULT_ANALYSIS_CONFIG.efficiencyMetrics,
      otherFactors: {
        ...prev.otherFactors,
        injuryStatus: DEFAULT_ANALYSIS_CONFIG.otherFactors?.injuryStatus ?? true,
        usageTrend: DEFAULT_ANALYSIS_CONFIG.otherFactors?.usageTrend ?? false,
        minutesTrend: DEFAULT_ANALYSIS_CONFIG.otherFactors?.minutesTrend ?? false,
        efficiencyTrend: DEFAULT_ANALYSIS_CONFIG.otherFactors?.efficiencyTrend ?? false,
      },
    }));
  };

  // Count enabled items in a section for badge display
  const getEnabledCount = (sectionKey: string): number => {
    const sectionConfig = config[sectionKey] as Record<string, boolean> | undefined;
    if (!sectionConfig) return 0;
    return Object.values(sectionConfig).filter((v) => v === true).length;
  };

  const getTotalCount = useMemo(
    () =>
      (sectionKey: string): number => {
        const sectionConfig = config[sectionKey] as Record<string, boolean> | undefined;
        if (!sectionConfig) return 0;
        const keys = Object.keys(sectionConfig);
        if (tier === 'admin') return keys.length;
        return keys.filter((k) => !isFactorDeprecated(`${sectionKey}.${k}`)).length;
      },
    [config, tier],
  );

  // Toggle all items in a section on/off (respects factor limit + cascade)
  const toggleAllInSection = (sectionKey: string, enable: boolean): void => {
    const sectionConfig = config[sectionKey] as Record<string, boolean> | undefined;
    if (!sectionConfig) return;

    // Prevent disabling all prop types — at least 1 must stay enabled
    if (!enable && sectionKey === 'propTypes') return;

    // Prevent disabling all stat windows — at least 1 must stay enabled
    if (!enable && sectionKey === 'playerStats') return;

    setConfig((prev) => {
      const prevSection = prev[sectionKey] as Record<string, boolean> | undefined;
      if (!prevSection) return prev;

      const newSectionConfig: Record<string, boolean> = { ...prevSection };

      if (enable) {
        // Only factors that count toward the plan's limit use up capacity; ones the plan lacks or
        // that are deprecated stay off, and non-factor keys turn on freely.
        let remaining = factorLimit - countEnabledFactors(prev);
        for (const key of Object.keys(prevSection)) {
          const def = getFactor(`${sectionKey}.${key}`);
          if (prevSection[key] === true) {
            newSectionConfig[key] = true; // already on, no cost
          } else if (def && (def.deprecatedReason || !canUseFactor(tier, def))) {
            newSectionConfig[key] = false;
          } else if (!def || !countsTowardLimit(def)) {
            newSectionConfig[key] = true;
          } else if (remaining > 0) {
            newSectionConfig[key] = true;
            remaining--;
          } else {
            newSectionConfig[key] = false; // can't fit
          }
        }
      } else {
        for (const key of Object.keys(prevSection)) {
          newSectionConfig[key] = false;
        }
      }

      let updated = {
        ...prev,
        [sectionKey]: newSectionConfig,
      } as AnalysisConfig;

      // Apply cascade disabling for all parent paths (handles children of toggled-off parents)
      if (!enable) {
        updated = applyCascadeDisableAll(updated);
      }

      return updated;
    });
  };

  // Toggle all prop types within a single category (e.g., "Game", "Alt Player")
  const toggleCategory = (category: string, enable: boolean): void => {
    const keysInCategory = PROP_TYPE_OPTIONS.filter((opt) => opt.category === category).map(
      (opt) => opt.key,
    );

    setConfig((prev) => {
      const prevPropTypes = prev.propTypes ?? {};
      const newPropTypes = { ...prevPropTypes };

      if (enable) {
        // Prop types are markets, not analysis factors, so the factor limit does not apply.
        for (const key of keysInCategory) {
          newPropTypes[key] = true;
        }
      } else {
        // Don't disable if it would leave ALL prop types off
        const otherEnabled = Object.entries(prevPropTypes).filter(
          ([k, v]) => v === true && !keysInCategory.includes(k),
        ).length;
        if (otherEnabled === 0) return prev;
        for (const key of keysInCategory) {
          newPropTypes[key] = false;
        }
      }

      return { ...prev, propTypes: newPropTypes } as AnalysisConfig;
    });
  };

  // Only minutesTrend is toggleable — usageTrend and efficiencyTrend are deprecated
  const ACTIVE_TREND_KEYS = ['minutesTrend'] as const;

  const toggleAllTrends = (_sectionKey: string, enable: boolean): void => {
    setConfig((prev) => {
      const prevOther = prev.otherFactors ?? {};
      const newOther = { ...prevOther };

      if (enable) {
        const currentEnabled = countEnabledFactors(prev);
        let remaining = factorLimit - currentEnabled;
        for (const key of ACTIVE_TREND_KEYS) {
          if (prevOther[key] === true) {
            newOther[key] = true;
          } else if (remaining > 0) {
            newOther[key] = true;
            remaining--;
          }
        }
      } else {
        for (const key of ACTIVE_TREND_KEYS) {
          newOther[key] = false;
        }
      }

      return {
        ...prev,
        otherFactors: newOther,
      } as AnalysisConfig;
    });
  };

  return {
    tier,
    upgradeFeature,
    setUpgradeFeature,
    expandedSections,
    toggleSection,
    toggleAllSections,
    factorLimit,
    enabledFactors,
    atFactorLimit,
    overFactorLimit,
    factorLimitProps,
    composedToggleProps,
    isLastStatWindow,
    updateConfig,
    resetToSaved,
    handleResetFactorsToDefaults,
    getEnabledCount,
    getTotalCount,
    toggleAllInSection,
    toggleCategory,
    toggleAllTrends,
  };
}
